import React from 'react'
import {Redirect} from 'react-router-dom'

import PageLoader from '@progyny/components/PageLoader'
import {
  useConsultationsMyAppointmentsQuery,
  useConsultationsProgramDetailQuery,
  type ConsultationsMyAppointmentsQuery,
} from '@progyny/generated/graphql'
import ConsultationPageLayout from '@progyny/pages/Consultations/components/ConsultationPageLayout'
import SingleSessionOverview from '@progyny/pages/Consultations/components/SingleSession/SingleSessionOverview'
import {FERTILITY_PROGRAM_SLUG} from '@progyny/pages/Consultations/constants'
import {
  filterUpcomingAppointments,
  filterPastAppointments,
  getAppointmentsBySlug,
} from '@progyny/utils/appointmentHelpers'

export const FERTILITY_SESSION_NUMBER = 1

interface FertilityOverviewProps {
  appointmentsData?: ConsultationsMyAppointmentsQuery
  onAppointmentsRefetch?: () => void
}

const FertilityOverview: React.FC<FertilityOverviewProps> = ({
  appointmentsData,
  onAppointmentsRefetch,
}) => {
  const {
    data: programData,
    loading: programLoading,
    refetch: refetchProgram,
  } = useConsultationsProgramDetailQuery({
    variables: {slug: FERTILITY_PROGRAM_SLUG},
  })

  const program = programData?.consultations?.program

  const handleDataRefetch = React.useCallback(() => {
    refetchProgram()
    onAppointmentsRefetch?.()
  }, [refetchProgram, onAppointmentsRefetch])

  if (programLoading) {
    return <PageLoader position={'center'} />
  }

  return (
    <ConsultationPageLayout
      consultationSlug='fertility'
      titleOverride={program?.consultation?.title}
      descriptionOverride={program?.consultation?.description}
    >
      {(
        _consultation,
        colors,
        _theme,
        _refetchConsultationDetail,
        onCalendarVisibilityChange,
        onTabsVisibilityChange
      ) => (
        <SingleSessionOverview
          programSlug={FERTILITY_PROGRAM_SLUG}
          sessionNumber={FERTILITY_SESSION_NUMBER}
          colors={colors}
          basePath='/consultations/fertility'
          showTabs={false}
          programData={programData}
          appointmentsData={appointmentsData}
          onDataRefetch={handleDataRefetch}
          onCalendarVisibilityChange={onCalendarVisibilityChange}
          onTabsVisibilityChange={onTabsVisibilityChange}
          isFertility
        />
      )}
    </ConsultationPageLayout>
  )
}

const FertilityConsultationPage: React.FC = () => {
  const {
    data: appointmentsData,
    loading,
    refetch: refetchAppointments,
  } = useConsultationsMyAppointmentsQuery({
    fetchPolicy: 'cache-and-network',
  })

  const programAppointments = getAppointmentsBySlug(
    FERTILITY_PROGRAM_SLUG,
    appointmentsData
  )

  const upcomingAppointments = filterUpcomingAppointments(programAppointments)
  const pastAppointments = filterPastAppointments(programAppointments)

  const hasAppointments =
    upcomingAppointments.length > 0 || pastAppointments.length > 0

  if (loading) {
    return <PageLoader position={'center'} />
  }

  if (hasAppointments) {
    return <Redirect to='/consultations/program/fertility-consultation' />
  }

  return (
    <FertilityOverview
      appointmentsData={appointmentsData}
      onAppointmentsRefetch={refetchAppointments}
    />
  )
}

export default FertilityConsultationPage
